import React, { useState, useEffect } from 'react';
import { MapPin, Mail, Phone, Globe, ArrowRight, Lock } from 'lucide-react';
import { ref, onValue } from 'firebase/database';
import { database } from '../../firebase/config';

export default function VenuesWidget({ user, bandId, setActiveTab, isPremium }) {
  const [recommendedVenues, setRecommendedVenues] = useState([]);
  const [bandProfile, setBandProfile] = useState(null);
  const [venuesLoading, setVenuesLoading] = useState(true);

  // Load band profile for venues filtering
  useEffect(() => {
    if (!user || !bandId) return;

    const bandRef = ref(database, `bands/${bandId}`);
    const unsubscribe = onValue(bandRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setBandProfile({
          location: data.location || null,
          region: data.region || null,
          genres: data.genres || []
        });
      }
    });

    return () => unsubscribe();
  }, [user, bandId]);

  // Load and score venues for recommendations
  useEffect(() => {
    if (!user || !bandId) return;

    const venuesRef = ref(database, 'venues');
    
    const unsubscribe = onValue(venuesRef, (snapshot) => {
      setVenuesLoading(true);
      
      if (snapshot.exists()) {
        const venuesData = snapshot.val();
        const venuesArray = Object.keys(venuesData).map(key => ({
          id: key,
          ...venuesData[key]
        }));

        const bandGenres = bandProfile?.genres || [];
        const bandRegion = bandProfile?.region;

        // Score each venue based on genre matches and region
        const scoredVenues = venuesArray.map(venue => {
          let score = 0;
          const genreMatches = [];

          if (venue.genres && bandGenres.length > 0) {
            venue.genres.forEach(g => {
              if (bandGenres.includes(g)) {
                score += 5;
                genreMatches.push(g);
              }
            });
          }

          if (bandRegion && venue.region === bandRegion) {
            score += 10;
          }

          if (venue.genreConfidence === 'high') {
            score += 2;
          } else if (venue.genreConfidence === 'medium') {
            score += 1;
          }

          return { ...venue, relevanceScore: score, genreMatches };
        });

        let topVenues;
        
        if (bandGenres.length > 0 || bandRegion) {
          const relevantVenues = scoredVenues
            .filter(v => v.relevanceScore > 0)
            .sort((a, b) => b.relevanceScore - a.relevanceScore);
          
          const topPool = relevantVenues.slice(0, 10);
          const shuffled = topPool.sort(() => Math.random() - 0.5);
          topVenues = shuffled.slice(0, 3);
        } else {
          const shuffled = scoredVenues.sort(() => Math.random() - 0.5);
          topVenues = shuffled
            .slice(0, 3)
            .map(v => ({ ...v, genreMatches: [] }));
        }

        setRecommendedVenues(topVenues);
      } else {
        setRecommendedVenues([]);
      }
      
      setVenuesLoading(false);
    });

    return () => unsubscribe();
  }, [user, bandId, bandProfile]);

  return (
    <div className="card-3d rounded-card p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold flex items-center gap-2">
          <MapPin className="w-4 h-4 text-brand" />
          {bandProfile?.genres?.length > 0 ? 'Recommended Venues' : 'Venues Near You'}
        </h2>
        <button
          onClick={() => setActiveTab('venues')}
          className="text-xs text-brand hover:text-brand-magenta transition flex items-center gap-1"
        >
          Browse All <ArrowRight className="w-3 h-3" />
        </button>
      </div>

      {!bandProfile?.region && !bandProfile?.genres?.length ? (
        <div className="card-3d rounded-card p-3 text-center">
          <MapPin className="w-6 h-6 mx-auto mb-2 text-text-subtle" />
          <p className="text-xs text-text-muted mb-2">Set your location and genres for personalized recommendations</p>
          <button
            onClick={() => setActiveTab('settings')}
            className="bg-brand hover:bg-brand-hover transition px-6 h-btn text-sm font-semibold rounded-pill"
          >
            Set Up Profile
          </button>
        </div>
      ) : venuesLoading ? (
        <div className="text-center py-4 text-text-subtle">
          <div className="w-6 h-6 border-3 border-brand border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-xs">Loading...</p>
        </div>
      ) : recommendedVenues.length === 0 ? (
        <div className="text-center py-4 text-text-subtle">
          <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-xs">No matching venues found</p>
        </div>
      ) : (
        <div className="space-y-2">
          {recommendedVenues.map(venue => (
            <div key={venue.id} className="bg-transparent border border-surface-border-light rounded-card-btn p-3 hover:border-surface-border transition">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm truncate">{venue.name}</h3>
                  <p className="text-xs text-text-subtle">
                    {venue.city}
                    {venue.region === bandProfile?.region && (
                      <span className="ml-1 text-brand">• Your region</span>
                    )}
                  </p>
                </div>
              </div>

              {/* Genre Matches */}
              {venue.genreMatches && venue.genreMatches.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {venue.genreMatches.slice(0, 3).map((genre, idx) => (
                    <span 
                      key={idx} 
                      className="bg-green-900/30 text-green-400 px-1.5 py-0.5 rounded text-xs"
                    >
                      {genre}
                    </span>
                  ))}
                  {venue.genreMatches.length > 3 && (
                    <span className="text-xs text-text-subtle">+{venue.genreMatches.length - 3}</span>
                  )}
                </div>
              )}

              {/* Contact Icons */}
              <div className="flex gap-2">
                {isPremium ? (
                  <>
                    {venue.email && (
                      <a
                        href={"mailto:" + venue.email}
                        className="bg-transparent hover:bg-brand transition p-2 rounded-full"
                        title="Email"
                      >
                        <Mail className="w-3 h-3" />
                      </a>
                    )}
                    {venue.phone && (
                      <a
                        href={"tel:" + venue.phone}
                        className="bg-transparent hover:bg-brand transition p-2 rounded-full"
                        title="Call"
                      >
                        <Phone className="w-3 h-3" />
                      </a>
                    )}
                    {venue.website && (
                      <a
                        href={venue.website.startsWith('http') ? venue.website : "https://" + venue.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-transparent hover:bg-brand transition p-2 rounded-full"
                        title="Website"
                      >
                        <Globe className="w-3 h-3" />
                      </a>
                    )}
                  </>
                ) : (
                  <div className="flex items-center gap-1 text-yellow-400 text-xs">
                    <Lock className="w-3 h-3" />
                    <span>Premium</span>
                  </div>
                )}
              </div>
            </div>
          ))}

          <button
            onClick={() => setActiveTab('venues')}
            className="w-full bg-brand hover:bg-brand-hover transition px-3 h-btn text-sm font-semibold flex items-center justify-center gap-1.5 rounded-pill"
          >
            Browse All Venues <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}
